import React, { useState, useMemo } from "react";
import {
  Button,
  Modal,
  Table,
  Badge,
  Tooltip,
  TextInput,
} from "flowbite-react";
import {
  HiCheckCircle,
  HiExclamation,
  HiInformationCircle,
  HiClipboardCopy,
  HiOutlineRefresh,
  HiSearch,
} from "react-icons/hi";
import { toast } from "../../../../toast";
import { gql, useMutation } from "@apollo/client";

const tabConfig = {
  success: {
    title: "Successfully Created",
    icon: <HiCheckCircle className="mr-1 text-green-600" />,
    color: "green",
  },
  duplicate: {
    title: "Duplicated Details",
    icon: <HiInformationCircle className="mr-1 text-yellow-600" />,
    color: "yellow",
  },
  error: {
    title: "Error Occurred",
    icon: <HiExclamation className="mr-1 text-red-600" />,
    color: "red",
  },
};

/* ---------------- MUTATION ---------------- */

const RESEND_INVITE_MUTATION = gql`
  mutation ResendStudentInvite($email: String!) {
    resendStudentInvite(email: $email)
  }
`;




/* ---------------- COMPONENT ---------------- */

const UploadResponseModal = ({
  isOpen,
  onClose,
  tab,
  setTab,
  successList = [],
  duplicateList = [],
  errorList = [],
}) => {
  const [search, setSearch] = useState("");
  const effectiveErrorList = Array.isArray(errorList) ? errorList : [];
  const [resendInvite] = useMutation(RESEND_INVITE_MUTATION);

  /* ---------------- FILTER LOGIC ---------------- */

  const filterByEmail = (list) =>
    list.filter((item) => {
      const email = item?.student?.email || item?.email || "";
      return email.toLowerCase().includes(search.toLowerCase());
    });

  const filteredSuccess = useMemo(
    () => filterByEmail(successList),
    [successList, search]
  );
  const filteredDuplicate = useMemo(
    () => filterByEmail(duplicateList),
    [duplicateList, search]
  );
  const filteredError = useMemo(
    () => filterByEmail(effectiveErrorList),
    [effectiveErrorList, search]
  );

  /* ---------------- ACTIONS ---------------- */

  const handleCopyEmails = () => {
    const emails = filteredError
      .map((item) => item?.student?.email || item?.email)
      .filter(Boolean)
      .join(",");

    if (emails) {
      navigator.clipboard.writeText(emails);
      toast.success("Emails copied to clipboard!");
    } else {
      toast.info("No emails available to copy.");
    }
  };

  const handleResend = async (email) => {
    try {
      const { data } = await resendInvite({ variables: { email } });
      toast.success(data.resendStudentInvite);
    } catch (err) {
      console.error(err);
      toast.error("Failed to resend invite.");
    }
  };

  /* ---------------- TABLE RENDER ---------------- */

  const renderTable = (items, showCopy = false, showResend = false) => {
    return (
      <>
        {showCopy && items.length > 0 && (
          <div className="flex justify-end mb-2">
            <Tooltip content="Copy All Emails">
              <Button color="light" onClick={handleCopyEmails}>
                <HiClipboardCopy className="mr-2" />
                Copy Emails
              </Button>
            </Tooltip>
          </div>
        )}

        <div className="overflow-x-auto max-h-[400px] rounded-lg shadow">
          <Table hoverable striped>
            <Table.Head className="sticky top-0 z-10 bg-white">
              <Table.HeadCell>Email</Table.HeadCell>
              <Table.HeadCell>Message</Table.HeadCell>
              {showResend && <Table.HeadCell>Resend Invite</Table.HeadCell>}
            </Table.Head>
            <Table.Body className="divide-y">
              {items.length > 0 ? (
                items.map((item, idx) => {
                  const email = item?.student?.email || item?.email || "N/A";
                  return (
                    <Table.Row key={idx}>
                      <Table.Cell>{email}</Table.Cell>
                      <Table.Cell>{item?.message || "N/A"}</Table.Cell>
                      {showResend && (
                        <Table.Cell>
                          <Tooltip content="Resend Invite Email">
                            <Button
                              color="light"
                              size="sm"
                              onClick={() => handleResend(email)}
                            >
                              <HiOutlineRefresh className="text-blue-600" />
                            </Button>
                          </Tooltip>
                        </Table.Cell>
                      )}
                    </Table.Row>
                  );
                })
              ) : (
                <Table.Row>
                  <Table.Cell
                    colSpan={showResend ? 3 : 2}
                    className="py-6 text-center text-gray-500"
                  >
                    No matching results.
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
      </>
    );
  };

  const tabList = [
    { key: "success", count: successList.length },
    { key: "duplicate", count: duplicateList.length },
    { key: "error", count: effectiveErrorList.length },
  ];

  /* ---------------- UI ---------------- */

  return (
    <Modal show={isOpen} onClose={onClose} size="5xl">
      <Modal.Header>📄 Bulk Upload Summary</Modal.Header>

      <Modal.Body>
        {/* ✅ TABS + RIGHT SEARCH */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex flex-wrap gap-2">
            {tabList.map(({ key, count }) => {
              const { icon, color } = tabConfig[key];
              return (
                <Button
                  key={key}
                  color={tab === key ? color : "light"}
                  onClick={() => setTab(key)}
                  className="flex items-center gap-2"
                >
                  {icon}
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                  <Badge color={color}>{count}</Badge>
                </Button>
              );
            })}
          </div>

          {/* ✅ SMALL RIGHT SEARCH BAR */}
          <div className="w-64">
            <TextInput
              icon={HiSearch}
              placeholder="Search email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* ✅ TITLE */}
        <h3 className="mb-3 text-xl font-semibold text-gray-700">
          {tabConfig[tab].title}
        </h3>

        {/* ✅ FILTERED TABLES */}
        {tab === "success" && renderTable(filteredSuccess, false, true)}
        {tab === "duplicate" && renderTable(filteredDuplicate, false, true)}
        {tab === "error" && renderTable(filteredError, true)}
      </Modal.Body>

      <Modal.Footer className="flex items-center justify-between w-full">
        <Button color="gray" onClick={onClose}>
          Close
        </Button>
        <Button
          color="red"
          onClick={() => {
            localStorage.removeItem("lastUploadResults");
            toast.success("Upload results cleared!");
            onClose();
          }}
        >
          Clear Stored Results
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UploadResponseModal;
